/**
 * Cart Form component
 * Handles cart form interactions for both cart page and cart drawer:
 * - Checkout submit loading state with double-submit guard
 * - Item quantity updates
 * - Item removal
 */
class CartForm extends HTMLElement {
  constructor() {
    super();
    this.isUpdating = false;
    this.isSubmitting = false;
    this.isApplyingDiscount = false;
    this.form = this.querySelector('form');

    this.onSubmit = this.onSubmit.bind(this);
    this.onClick = this.onClick.bind(this);
    this.onChange = this.onChange.bind(this);
    this.syncStickySummarySpacer = this.syncStickySummarySpacer.bind(this);
  }

  connectedCallback() {
    if (this.form) {
      this.form.addEventListener('submit', this.onSubmit);
    }

    this.addEventListener('click', this.onClick);
    this.addEventListener('change', this.onChange);

    // Handle cart note toggle
    this.setupCartNoteToggle();

    // Handle TNC checkbox validation
    this.setupTNCValidation();

    window.addEventListener('resize', this.syncStickySummarySpacer);
    document.addEventListener('cart:updated', this.syncStickySummarySpacer);
    requestAnimationFrame(this.syncStickySummarySpacer);

    if (this.isCartPage) {
      this.trackCartView();
    }
  }

  disconnectedCallback() {
    if (this.form) {
      this.form.removeEventListener('submit', this.onSubmit);
    }

    this.removeEventListener('click', this.onClick);
    this.removeEventListener('change', this.onChange);
    window.removeEventListener('resize', this.syncStickySummarySpacer);
    document.removeEventListener('cart:updated', this.syncStickySummarySpacer);
  }

  onSubmit(e) {
    // Find the button that triggered the submit
    const submitButton =
      e.submitter ||
      this.querySelector('button[type="submit"], input[type="submit"]');
    if (!submitButton) return;

    if (this.isSubmitting || submitButton.classList.contains('loading')) {
      e.preventDefault();
      return;
    }

    this.isSubmitting = true;
    submitButton.classList.add('loading');
    submitButton.setAttribute('aria-disabled', 'true');
    submitButton.style.pointerEvents = 'none';
  }

  onClick(e) {
    const applyDiscountTrigger = e.target.closest(
      '[data-action="apply-discount"]',
    );
    if (applyDiscountTrigger) {
      e.preventDefault();

      const inputSelector = applyDiscountTrigger.dataset.discountInput;
      const input = inputSelector
        ? this.querySelector(inputSelector)
        : this.querySelector('input[name="discount_code"]');
      const code = input?.value?.trim();

      if (!code) return;

      this.applyDiscountCode(code, applyDiscountTrigger);
      return;
    }

    const removeDiscountTrigger = e.target.closest(
      '[data-action="remove-discount"]',
    );
    if (removeDiscountTrigger) {
      e.preventDefault();
      this.removeDiscountCode(removeDiscountTrigger);
      return;
    }

    const removePointsTrigger = e.target.closest(
      '[data-action="remove-points"]',
    );
    if (removePointsTrigger) {
      e.preventDefault();
      // <cart-customer-points> owns the removal logic (clears the redemption
      // cart attributes) — this button lives in cart-total's discount list,
      // outside that element, so we ask it to remove itself instead of
      // duplicating the cart/update.js call here.
      document.dispatchEvent(new CustomEvent('lensza:points-remove-requested'));
      return;
    }

    const removeBtn = e.target.closest('[data-action="remove"]');
    if (!removeBtn) return;

    e.preventDefault();
    const lineItem = removeBtn.closest('[data-line-item-key]');
    const key = lineItem?.dataset.lineItemKey;
    if (!key) return;

    this.updateQuantity(key, 0);
  }

  onChange(e) {
    const qtyInput = e.target.closest('input-qty');
    if (!qtyInput) return;

    const lineItem = qtyInput.closest('[data-line-item-key]');
    const key = lineItem?.dataset.lineItemKey;
    if (!key) return;

    this.updateQuantity(key, e.target.value);
  }

  get drawer() {
    return this.closest('cart-drawer');
  }

  get loader() {
    return this.querySelector('.cart-drawer__loading, .cart-page__loading');
  }

  get cartPageSection() {
    return this.closest('[data-cart-page-section]');
  }

  get isCartPage() {
    return Boolean(this.cartPageSection);
  }

  trackCartView() {
    if (typeof window.LenszaAnalytics?.trackCartView === 'function') {
      window.LenszaAnalytics.trackCartView('page');
      return;
    }

    document.addEventListener(
      'lensza:analytics-ready',
      () => window.LenszaAnalytics?.trackCartView('page'),
      { once: true },
    );
  }

  syncStickySummarySpacer() {
    const summaries = this.querySelectorAll(
      '[data-cart-summary-sticky="true"]',
    );
    const sectionRoot = this.cartPageSection;
    const footer = document.querySelector('.footer');

    if (!sectionRoot) return;

    let stickyOffset = 0;

    summaries.forEach((summary) => {
      const cartPageSection = summary.closest('[data-cart-page-section]');

      if (window.innerWidth < 1024) {
        cartPageSection?.classList.add('cart-page--has-sticky-summary-mobile');
        stickyOffset = Math.max(stickyOffset, summary.offsetHeight);
      } else {
        cartPageSection?.classList.remove(
          'cart-page--has-sticky-summary-mobile',
        );
      }
    });

    if (window.innerWidth < 1024 && stickyOffset > 0) {
      sectionRoot.classList.add('cart-page--has-sticky-summary-mobile');
      footer?.style.setProperty(
        '--cart-summary-sticky-offset',
        `${stickyOffset}px`,
      );
      footer?.style.setProperty(
        'padding-bottom',
        'var(--cart-summary-sticky-offset)',
      );
    } else {
      sectionRoot.classList.remove('cart-page--has-sticky-summary-mobile');
      footer?.style.removeProperty('--cart-summary-sticky-offset');
      footer?.style.removeProperty('padding-bottom');
    }
  }

  async refreshCartPageSection() {
    const sectionRoot = this.cartPageSection;
    const sectionId = sectionRoot?.dataset.sectionId;

    if (!sectionRoot || !sectionId) {
      window.location.reload();
      return;
    }

    const response = await fetch(
      `${window.location.pathname}?sections=${encodeURIComponent(sectionId)}`,
    );
    const data = await response.json();
    const html = data[sectionId];

    if (!html) {
      window.location.reload();
      return;
    }

    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const newSectionRoot = doc.querySelector('[data-cart-page-section]');

    if (!newSectionRoot) {
      window.location.reload();
      return;
    }

    if (typeof window.wakaMorph === 'function') {
      window.wakaMorph(
        sectionRoot,
        newSectionRoot,
        {},
        {
          context: 'cart-form',
          root: sectionRoot,
          skipIfVisible: true,
        },
      );
      requestAnimationFrame(this.syncStickySummarySpacer);
    } else {
      sectionRoot.replaceWith(newSectionRoot);
    }
  }

  async updateQuantity(key, qty) {
    if (this.isUpdating) return;
    this.isUpdating = true;
    const activeLoader = this.drawer?.loader || this.loader;
    activeLoader?.classList.add('active');

    try {
      const resp = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: parseInt(qty, 10) }),
      });
      const cart = await resp.json();

      const source = this.drawer ? 'cart-drawer' : 'cart-page';
      document.dispatchEvent(
        new CustomEvent('cart:updated', {
          detail: { count: cart.item_count, source },
        }),
      );

      if (this.drawer && typeof this.drawer.fetchCart === 'function') {
        await this.drawer.fetchCart();
      } else if (this.isCartPage) {
        await this.refreshCartPageSection();
      } else {
        window.location.reload();
      }
    } catch (err) {
      console.error('Update quantity failed', err);
    } finally {
      this.isUpdating = false;
      activeLoader?.classList.remove('active');
    }
  }

  async applyDiscountCode(code, triggerEl) {
    if (this.isApplyingDiscount) return;
    this.isApplyingDiscount = true;

    const button = triggerEl?.querySelector('button');
    const discountForm = triggerEl?.closest('[data-discount-form]');
    const errorSpan = discountForm?.querySelector('[data-discount-error]');
    const inputField = discountForm?.querySelector('[name="discount_code"]');
    const activeLoader = this.drawer?.loader || this.loader;

    // Clear previous error state
    if (errorSpan) {
      errorSpan.textContent = '';
      errorSpan.classList.add('hidden');
    }
    inputField?.classList.remove('error');

    if (button) {
      button.classList.add('loading');
      button.disabled = true;
      button.setAttribute('aria-disabled', 'true');
    }

    activeLoader?.classList.add('active');

    try {
      // Step 1: apply via the discount endpoint (works for all discount types)
      const applyResponse = await fetch(
        `/discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(window.location.pathname)}`,
        { method: 'GET', credentials: 'same-origin' },
      );

      if (!applyResponse.ok) throw new Error('Failed to apply discount code');

      // Step 2: fetch /cart.js to verify — its discount_codes array only
      // contains successfully applied codes, so a missing entry means the code
      // was invalid or inapplicable.
      const cart = await fetch(`${window.Shopify.routes.root}cart.js`, {
        credentials: 'same-origin',
      }).then((r) => r.json());

      const applied = cart.discount_codes?.some(
        (d) =>
          d.code.toLowerCase() === code.toLowerCase() && d.applicable === true,
      );

      if (!applied) {
        if (errorSpan) {
          errorSpan.textContent =
            errorSpan.dataset.invalidMessage ||
            'This discount code is not valid';
          errorSpan.classList.remove('hidden');
        }
        inputField?.classList.add('error');
        return;
      }

      document.dispatchEvent(
        new CustomEvent('cart:updated', {
          detail: {
            count: cart.item_count,
            source: this.drawer ? 'cart-drawer' : 'cart-page',
          },
        }),
      );

      if (this.drawer && typeof this.drawer.fetchCart === 'function') {
        await this.drawer.fetchCart();
      } else if (this.isCartPage) {
        await this.refreshCartPageSection();
      } else {
        window.location.reload();
      }
    } catch (err) {
      console.error('Apply discount failed', err);
      if (errorSpan) {
        errorSpan.textContent =
          errorSpan.dataset.invalidMessage || 'This discount code is not valid';
        errorSpan.classList.remove('hidden');
      }
      inputField?.classList.add('error');
    } finally {
      this.isApplyingDiscount = false;
      if (button) {
        button.classList.remove('loading');
        button.disabled = false;
        button.removeAttribute('aria-disabled');
      }
      activeLoader?.classList.remove('active');
    }
  }

  async removeDiscountCode(triggerEl) {
    if (this.isApplyingDiscount) return;
    this.isApplyingDiscount = true;

    triggerEl.disabled = true;
    const activeLoader = this.drawer?.loader || this.loader;
    activeLoader?.classList.add('active');

    try {
      // Collect all currently displayed discount codes except the one being removed,
      // then send the remaining list. An empty string removes all codes.
      const codeToRemove = triggerEl.dataset.code;
      const remainingCodes = [
        ...document.querySelectorAll('[data-action="remove-discount"]'),
      ]
        .map((btn) => btn.dataset.code)
        .filter((code) => code && code !== codeToRemove);

      const response = await fetch(
        `${window.Shopify.routes.root}cart/update.js`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ discount: remainingCodes.join(',') }),
        },
      );

      if (!response.ok) throw new Error('Failed to remove discount code');

      const cart = await response.json();
      document.dispatchEvent(
        new CustomEvent('cart:updated', {
          detail: {
            count: cart.item_count,
            source: this.drawer ? 'cart-drawer' : 'cart-page',
          },
        }),
      );

      if (this.drawer && typeof this.drawer.fetchCart === 'function') {
        await this.drawer.fetchCart();
      } else if (this.isCartPage) {
        await this.refreshCartPageSection();
      } else {
        window.location.reload();
      }
    } catch (err) {
      console.error('Remove discount failed', err);
    } finally {
      this.isApplyingDiscount = false;
      triggerEl.disabled = false;
      activeLoader?.classList.remove('active');
    }
  }

  setupCartNoteToggle() {
    const cartNoteToggle = this.querySelector('input[name="note_checkbox"]');
    const cartNoteTextarea = this.querySelector('textarea[name="note"]');
    const cartNoteWrapper = cartNoteTextarea?.closest('.cart-note-textarea');

    if (cartNoteToggle && cartNoteWrapper) {
      cartNoteWrapper.classList.toggle('active', cartNoteToggle.checked);

      cartNoteToggle.addEventListener('change', (e) => {
        cartNoteWrapper.classList.toggle('active', e.target.checked);
        if (!e.target.checked && cartNoteTextarea) {
          cartNoteTextarea.value = '';
        }
      });
    }
  }

  setupTNCValidation() {
    // Handle TNC validation for cart page
    const cartTNC = this.querySelector('#CartTNC');
    const checkoutButton = this.querySelector(
      '#CheckoutButton button[type="submit"]',
    );

    if (cartTNC && checkoutButton) {
      // Set initial state
      checkoutButton.disabled = !cartTNC.checked;

      cartTNC.addEventListener('change', (e) => {
        checkoutButton.disabled = !e.target.checked;
      });
    }

    // Handle TNC validation for cart drawer
    const cartTNCDrawer = this.querySelector('#CartTNCDrawer');
    const checkoutButtonDrawer = this.querySelector(
      '#CheckoutButtonDrawer button[type="submit"]',
    );

    if (cartTNCDrawer && checkoutButtonDrawer) {
      // Set initial state
      checkoutButtonDrawer.disabled = !cartTNCDrawer.checked;

      cartTNCDrawer.addEventListener('change', (e) => {
        checkoutButtonDrawer.disabled = !e.target.checked;
      });
    }
  }
}

if (!customElements.get('cart-form')) {
  customElements.define('cart-form', CartForm);
}
